import React, {useCallback} from 'react';
import {useTranslation} from 'react-i18next';
import EditorLayout from '../../../../components/Layouts/EditorLayout';
import SMCTextInput from '../../../../components/Simple/SMCTextInput';
import {SMCSelect, SMCOption} from '../../../../components/Simple/SMCSelect';
import {SMCCheckbox} from '../../../../components/Simple/SMCCheckbox';
import Indentation from '../../../../components/Simple/Indentation';
import SMCNumberInput from '../../../../components/Simple/SMCNumberInput';
import {SystemSettingsGetSchema} from '../../../../api/DBSchema/SystemSettings';

const pageSchema = SystemSettingsGetSchema.pages.logging;
const pageId = pageSchema.pageId;

function SystemSettingsLogging({
    systemSettingsObject,
    setSystemSettingsData
}) {
    const { t } = useTranslation();

    const pageData = systemSettingsObject[pageId];
    const setPageValue = useCallback((paramName, value) => {
        setSystemSettingsData([pageId, paramName], value);
    }, [setSystemSettingsData]);

    const logLevel              = pageData[pageSchema.logLevel];
    const logLevelValues        = pageSchema.logLevelValues;

    const isSyslogClientEnabled = pageData[pageSchema.isSyslogClientEnabled];
    const syslogClientHost      = pageData[pageSchema.syslogClientHost];
    const syslogClientPort      = pageData[pageSchema.syslogClientPort];
    const isPurgeOldLogs        = pageData[pageSchema.isPurgeOldLogs];
    const oldLogsDays           = pageData[pageSchema.oldLogsDays];
    const isOldLogsArchive      = pageData[pageSchema.isOldLogsArchive];

    return (
        <EditorLayout>
            <SMCSelect
                value={logLevel}
                onChange={e => setPageValue(pageSchema.logLevel, e.target.value)}
                label={t('SMP.SETTINGS.LOGGING.LOG_LEVEL')}
            >
                <SMCOption value={logLevelValues.debug}>{t('SMP.SETTINGS.LOGGING.LOG_LEVEL.DEBUG')}</SMCOption>
                <SMCOption value={logLevelValues.info}>{t('SMP.SETTINGS.LOGGING.LOG_LEVEL.INFO')}</SMCOption>
                <SMCOption value={logLevelValues.notice}>{t('SMP.SETTINGS.LOGGING.LOG_LEVEL.NOTICE')}</SMCOption>
                <SMCOption value={logLevelValues.warning}>{t('SMP.SETTINGS.LOGGING.LOG_LEVEL.WARNING')}</SMCOption>
                <SMCOption value={logLevelValues.error}>{t('SMP.SETTINGS.LOGGING.LOG_LEVEL.ERROR')}</SMCOption>
                <SMCOption value={logLevelValues.critical}>{t('SMP.SETTINGS.LOGGING.LOG_LEVEL.CRITICAL')}</SMCOption>
                <SMCOption value={logLevelValues.alert}>{t('SMP.SETTINGS.LOGGING.LOG_LEVEL.ALERT')}</SMCOption>
                <SMCOption value={logLevelValues.emergency}>{t('SMP.SETTINGS.LOGGING.LOG_LEVEL.EMERGENCY')}</SMCOption>
            </SMCSelect>
            <SMCCheckbox
                label={t('SMP.SETTINGS.LOGGING.LINE1')}
                tooltip={t('SMP.SETTINGS.LOGGING.LINE1.TOOLTIP')}
                checked={isSyslogClientEnabled}
                onChange={e => setPageValue(pageSchema.isSyslogClientEnabled, e.target.checked)}
            />
            {
                isSyslogClientEnabled && (
                    <Indentation>
                        <SMCTextInput
                            value={syslogClientHost}
                            onChange={e => setPageValue(pageSchema.syslogClientHost, e.target.value)}
                            label={t('SMP.SETTINGS.LOGGING.SYSLOG_SERVER')}
                        />
                        <SMCNumberInput
                            value={syslogClientPort}
                            onChange={e => setPageValue(pageSchema.syslogClientPort, parseInt(e.target.value))}
                            label={t('SMP.SETTINGS.LOGGING.SYSLOG_PORT')}
                            inline
                        /><span>(UDP)</span>
                    </Indentation>
                )
            }
            <SMCCheckbox
                label={t('SMP.SETTINGS.LOGGING.LINE1.PURGE_OLD_LOGS')}
                checked={isPurgeOldLogs}
                onChange={e => setPageValue(pageSchema.isPurgeOldLogs, e.target.checked)}
            />
            {
                isPurgeOldLogs && (
                    <Indentation>
                        <SMCNumberInput
                            value={oldLogsDays}
                            onChange={e => setPageValue(pageSchema.oldLogsDays, parseInt(e.target.value))}
                            label={t('SMP.SETTINGS.LOGGING.OLDER_THAN')}
                        />
                        <SMCSelect
                            value={isOldLogsArchive ? 'ARCHIVE' : 'DELETE'}
                            onChange={e => setPageValue(pageSchema.isOldLogsArchive, e.target.value === 'ARCHIVE')}
                            label={t('SMP.SETTINGS.LOGGING.ACTION')}
                            tooltip={<>{t('SMP.SETTINGS.LOGGING.ACTION.TOOLTIP.LINE1')}<br /><b>{t('SMP.SETTINGS.LOGGING.ACTION.TOOLTIP.LINE2')}</b><br />{t('SMP.SETTINGS.LOGGING.ACTION.TOOLTIP.LINE3')}<br /><b>{t('SMP.SETTINGS.LOGGING.ACTION.TOOLTIP.LINE4')}</b></>}
                        >
                            <SMCOption value="ARCHIVE">{t('SMP.ARCHIVE')}</SMCOption>
                            <SMCOption value="DELETE">{t('SMC_GEN_DELETE')}</SMCOption>
                        </SMCSelect>
                    </Indentation>
                )
            }
        </EditorLayout>
    )
}

export default SystemSettingsLogging;